import { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Stack,
  Divider,
  CircularProgress,
  Grid,
  Chip,
} from '@mui/material';
import ContactMailIcon from '@mui/icons-material/ContactMail';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import BusinessIcon from '@mui/icons-material/Business';
import MessageIcon from '@mui/icons-material/Message';
import config from '../../Config';
import { useParams, useNavigate } from 'react-router-dom';

export default function LeadDetail() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [lead, setLead] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchLeadDetail = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${config.apiBaseUrl}/admin/get-leads/${id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      const data = await response.json();
      if (response.ok && data.success) {
        setLead(data.data);
      } else {
        setError('Failed to load lead details.');
      }
    } catch (err) {
      setError('An error occurred while fetching lead details.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLeadDetail();
  }, [id]);

  const formatDate = (dateStr) =>
    new Date(dateStr).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });

  if (loading) {
    return (
      <Box
        sx={{
          minHeight: '80vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: '#f8fafc',
        }}
      >
        <CircularProgress size={48} />
      </Box>
    );
  }

  if (error || !lead) {
    return (
      <Box
        sx={{
          p: 3,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '80vh',
          bgcolor: '#f8fafc',
        }}
      >
        <Typography variant="h6" color="error" gutterBottom>
          {error || 'Lead not found.'}
        </Typography>
        <ArrowBackIcon
          onClick={() => navigate('/leads')}
          sx={{
            cursor: 'pointer',
            color: 'primary.main',
            fontSize: 36,
            '&:hover': { color: 'primary.dark', transform: 'scale(1.1)' },
            transition: 'all 0.2s ease',
          }}
        />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        px: { xs: 2, sm: 4, md: 6 },
        py: 4,
        bgcolor: '#f8fafc',
        minHeight: '100vh',
      }}
    >
      <Stack direction="row" alignItems="center" justifyContent="space-between" mb={3}>
        <Stack direction="row" alignItems="center" spacing={2}>
          <ArrowBackIcon
            onClick={() => navigate('/leads')}
            sx={{
              cursor: 'pointer',
              color: 'primary.main',
              fontSize: 30,
              '&:hover': { color: 'primary.dark', transform: 'scale(1.1)' },
              transition: 'all 0.2s ease',
            }}
          />

          <Typography
            variant="h5"
            fontWeight="bold"
            sx={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              backgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
            Lead Details
          </Typography>
        </Stack>

        <Chip
          label={`Lead ID: ${lead._id}`}
          color="primary"
          variant="outlined"
          sx={{ fontWeight: 'bold' }}
        />
      </Stack>

      <Paper
        elevation={3}
        sx={{
          borderRadius: 3,
          p: { xs: 2, sm: 3, md: 4 },
          background: 'linear-gradient(180deg, #ffffff 0%, #f3f4f6 100%)',
          boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
        }}
      >
        <Stack direction="row" alignItems="center" spacing={2} mb={3}>
          <ContactMailIcon color="primary" sx={{ fontSize: 36 }} />
          <Box>
            <Typography variant="h5" fontWeight="bold">
              {lead.name || '-'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Submitted on {formatDate(lead.createdAt)}
            </Typography>
          </Box>
        </Stack>

        <Divider sx={{ mb: 3 }} />

        <Grid container spacing={3}>
          <Grid item xs={12} sm={6} md={4}>
            <Paper
              elevation={0}
              sx={{
                p: 2,
                borderRadius: 2,
                bgcolor: '#fff',
                height: '100%',
              }}
            >
              <Stack direction="row" alignItems="center" spacing={1}>
                <EmailIcon color="primary" />
                <Typography fontWeight="bold">Email</Typography>
              </Stack>
              <Typography variant="body2" sx={{ mt: 1 }}>
                {lead.email || '-'}
              </Typography>
            </Paper>
          </Grid>

          <Grid item xs={12} sm={6} md={4}>
            <Paper
              elevation={0}
              sx={{
                p: 2,
                borderRadius: 2,
                bgcolor: '#fff',
                height: '100%',
              }}
            >
              <Stack direction="row" alignItems="center" spacing={1}>
                <PhoneIcon color="primary" />
                <Typography fontWeight="bold">Phone</Typography>
              </Stack>
              <Typography variant="body2" sx={{ mt: 1 }}>
                {lead.number || '-'}
              </Typography>
            </Paper>
          </Grid>

          <Grid item xs={12} sm={6} md={4}>
            <Paper
              elevation={0}
              sx={{
                p: 2,
                borderRadius: 2,
                bgcolor: '#fff',
                height: '100%',
              }}
            >
              <Stack direction="row" alignItems="center" spacing={1}>
                <BusinessIcon color="primary" />
                <Typography fontWeight="bold">Organisation</Typography>
              </Stack>
              <Typography variant="body2" sx={{ mt: 1 }}>
                {lead.organisation || '-'}
              </Typography>
            </Paper>
          </Grid>

          <Grid item xs={12}>
            <Paper
              elevation={0}
              sx={{
                p: 2,
                borderRadius: 2,
                bgcolor: '#fff',
              }}
            >
              <Stack direction="row" alignItems="center" spacing={1} mb={1}>
                <MessageIcon color="primary" />
                <Typography fontWeight="bold">Message</Typography>
              </Stack>
              <Typography
                variant="body2"
                sx={{
                  whiteSpace: 'pre-line',
                  bgcolor: '#f9fafb',
                  p: 2,
                  borderRadius: 2,
                }}
              >
                {lead.message || '-'}
              </Typography>
            </Paper>
          </Grid>
        </Grid>

        <Divider sx={{ my: 4 }} />

        <Stack direction="row" alignItems="center" spacing={1}>
          <CalendarTodayIcon fontSize="small" color="action" />
          <Typography variant="body2" color="text.secondary">
            Created At: {formatDate(lead.createdAt)}
          </Typography>
        </Stack>
      </Paper>
    </Box>
  );
}
